import { GiExitDoor } from "react-icons/gi";
import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from '../UserContext';
import MenuTopo from "../MenuTopo/index";
import ModalProdutos from "../ModalProdutos/index";
import ModalCaixa from "../ModalCaixa/index"
import ModalXml from "../ModalXML/index";
import ModalRelatorios from "../ModalRelatorios/index";
import ModalConfigEmpresa from "../ModalConfig/index";
import "./index.css";

const fmt = (v) => Number(v || 0).toFixed(2).replace(".", ",");

const FORMAS_PAGAMENTO = [
  { key: "DINHEIRO", label: "Dinheiro" },
  { key: "DEBITO", label: "Debito" },
  { key: "CREDITO", label: "Credito" },
  { key: "VOUCHER", label: "Voucher" },
  { key: "PIX", label: "Pix" },
  { key: "OUTROS", label: "Outros" },
];

export default function Pdv() {
  const { userInfo } = useUser();
  const navigate = useNavigate();

  // ---------- Autenticação / sessão ----------
  const credentials = btoa(`${userInfo.alias};${userInfo.venLogin}:${userInfo.venSenha}`);

  useEffect(() => {
    if (!userInfo?.url || !userInfo?.alias || !userInfo?.venLogin) {
      navigate("/", { replace: true });
    }
  }, [userInfo, navigate]);

  // ---------- Produtos (API) ----------
  const [produtos, setProdutos] = useState([]);
  const [loadingProdutos, setLoadingProdutos] = useState(false);

  const fetchProdutos = useCallback(async () => {
    if (!userInfo?.url || !userInfo?.alias || !userInfo?.venLogin || !userInfo?.venSenha) {
      return;
    }

    try {
      setLoadingProdutos(true);

      const res = await fetch(`${userInfo.url}/produto/WenAPP`, {
        headers: {
          'Content-Type': 'application/json',
          'empresa': userInfo.alias,
          'Authorization': `Basic ${credentials}`,
        }
      });

      const text = await res.text();
      const json = JSON.parse(text);
      setProdutos(json);
    } catch (err) {
      console.error('Erro ao buscar PRODUTOS:', err);
    } finally {
      setLoadingProdutos(false);
    }
  }, [userInfo, credentials]);

  useEffect(() => {
    fetchProdutos();
  }, [fetchProdutos]);

  // ---------- Estados do PDV ----------
  const [busca, setBusca] = useState("");
  const [descricao, setDescricao] = useState("");
  const [quantidade, setQuantidade] = useState("");
  const [valor, setValor] = useState("");
  const [produtoAtual, setProdutoAtual] = useState(null);
  const [sugestoes, setSugestoes] = useState([]);
  const [itens, setItens] = useState([]);
  const [erro, setErro] = useState("");
  const [desconto, setDesconto] = useState("0");

  // ---------- Menu / Modal ----------
  const [modalAberto, setModalAberto] = useState(null);
  const [modalPagamentoAberto, setModalPagamentoAberto] = useState(false);

  // ---------- Pagamento ----------
  const [pagamentos, setPagamentos] = useState(
    FORMAS_PAGAMENTO.reduce((acc, f) => ({ ...acc, [f.key]: "" }), {})
  );
  const [erroPagamento, setErroPagamento] = useState("");

  const buscaRef = useRef(null);
  const qtdRef = useRef(null);
  const valorRef = useRef(null);
  const buscaTimeoutRef = useRef(null);

  useEffect(() => {
    buscaRef.current?.focus();
  }, []);

  const limparCampos = useCallback(() => {
    setBusca("");
    setDescricao("");
    setQuantidade("");
    setValor("");
    setProdutoAtual(null);
    setSugestoes([]);
  }, []);

  const focarBusca = useCallback(() => {
    limparCampos();
    setTimeout(() => buscaRef.current?.focus(), 50);
  }, [limparCampos]);

  // ---------- Busca unificada (nome / código / código de barras) ----------
  const selecionarSugestao = (produto) => {
    setProdutoAtual(produto);
    setBusca(produto.proDescricao);
    setDescricao(produto.proDescricao);
    setValor(String(produto.proVlrVenda));
    setQuantidade("1");
    setSugestoes([]);
    setErro("");
    setTimeout(() => {
      qtdRef.current?.focus();
      qtdRef.current?.select();
    }, 50);
  };

  const buscarUnificado = (texto) => {
    setBusca(texto);
    setErro("");

    if (buscaTimeoutRef.current) {
      clearTimeout(buscaTimeoutRef.current);
    }

    if (!texto.trim()) {
      setSugestoes([]);
      setDescricao("");
      setProdutoAtual(null);
      return;
    }

    buscaTimeoutRef.current = setTimeout(() => {
      const termo = texto.trim().toLowerCase();

      const resultados = produtos
        .filter(
          (p) =>
            p.proDescricao?.toLowerCase().includes(termo) ||
            String(p.proCodInterno).toLowerCase().includes(termo) ||
            String(p.proCodBarras).toLowerCase().includes(termo)
        )
        .slice(0, 8);

      setSugestoes(resultados);

      const exato = produtos.find(
        (p) => String(p.proCodInterno) === termo || String(p.proCodBarras) === termo
      );
      if (exato) {
        selecionarSugestao(exato);
      }
    }, 400);
  };

  const handleKeyDownBusca = (e) => {
    if (e.key === "Enter") {
      if (buscaTimeoutRef.current) {
        clearTimeout(buscaTimeoutRef.current);
      }

      if (sugestoes.length === 1) {
        selecionarSugestao(sugestoes[0]);
        return;
      }

      const termo = busca.trim();
      if (!termo) return;

      const encontrado = produtos.find(
        (p) =>
          String(p.proCodInterno) === termo ||
          String(p.proCodBarras) === termo ||
          p.proDescricao?.toLowerCase() === termo.toLowerCase()
      );

      if (encontrado) {
        selecionarSugestao(encontrado);
      } else {
        setErro("Produto não encontrado.");
        setDescricao("");
        setProdutoAtual(null);
      }
    }
  };

  useEffect(() => {
    return () => {
      if (buscaTimeoutRef.current) {
        clearTimeout(buscaTimeoutRef.current);
      }
    };
  }, []);

  // ---------- Itens da venda ----------
  const adicionarItem = () => {
    const qtdNum = parseFloat(String(quantidade).replace(",", "."));
    const valorNum = parseFloat(String(valor).replace(",", "."));

    if (!produtoAtual) {
      setErro("Selecione um produto antes de adicionar.");
      return;
    }
    if (!qtdNum || qtdNum <= 0) {
      setErro("Quantidade deve ser maior que zero.");
      qtdRef.current?.focus();
      return;
    }
    if (!valorNum || valorNum <= 0) {
      setErro("Valor de venda não pode ser zero.");
      valorRef.current?.focus();
      return;
    }

    const novoItem = {
      id: Date.now(),
      produtoId: produtoAtual.proId,
      produto: produtoAtual.proDescricao,
      qtd: qtdNum,
      valor: valorNum,
      total: qtdNum * valorNum,
      removido: false,
    };

    setItens((prev) => [...prev, novoItem]);
    setErro("");
    focarBusca();
  };

  const toggleRemovido = (id) => {
    setItens((prev) =>
      prev.map((it) => (it.id === id ? { ...it, removido: !it.removido } : it))
    );
  };

  const totalCompra = itens
    .filter((i) => !i.removido)
    .reduce((sum, i) => sum + i.total, 0);

  const descontoNum = parseFloat(String(desconto).replace(",", ".")) || 0;
  const totalComDesconto = Math.max(totalCompra - descontoNum, 0);

  // ---------- Pagamento ----------
  const dinheiroRef = useRef(null);

  useEffect(() => {
    if (modalPagamentoAberto) {
      setTimeout(() => {
        dinheiroRef.current?.focus();
        dinheiroRef.current?.select();
      }, 50);
    }
  }, [modalPagamentoAberto]);

  const totalPago = FORMAS_PAGAMENTO.reduce((sum, f) => {
    const v = parseFloat(String(pagamentos[f.key]).replace(",", ".")) || 0;
    return sum + v;
  }, 0);

  const troco = Math.max(totalPago - totalComDesconto, 0);
  const faltaPagar = Math.max(totalComDesconto - totalPago, 0);

  const abrirModalPagamento = useCallback(() => {
    const itensValidos = itens.filter((i) => !i.removido);
    if (itensValidos.length === 0) {
      setErro("Não há itens para finalizar.");
      return;
    }
    setErro("");
    setErroPagamento("");
    setModalPagamentoAberto(true);
  }, [itens]);

  const voltarDoPagamento = useCallback(() => {
    setModalPagamentoAberto(false);
    setErroPagamento("");
  }, []);

  const handlePagamentoChange = useCallback((key, value) => {
    setPagamentos((prev) => ({ ...prev, [key]: value }));
    setErroPagamento("");
  }, []);

  const finalizarVenda = useCallback(() => {
    if (totalPago < totalComDesconto) {
      setErroPagamento(
        `O valor pago (R$ ${fmt(totalPago)}) é menor que o total da compra (R$ ${fmt(totalComDesconto)}).`
      );
      return;
    }

    const itensValidos = itens.filter((i) => !i.removido);
    const itensValidosFormatados = itensValidos.map((it) => ({
      "ICU_DESCRICAO": it.produto,
      "ICU_QTD_PRODUTO": it.qtd,
      "ICU_STATUS": "Pendente",
      "ICU_VLR_PRODUTO": it.valor,
      "PRO_ID": it.produtoId
    }));

    const hoje = new Date();
    const vencimento = hoje.toISOString().split("T")[0];

    const pagamentosFormatados = FORMAS_PAGAMENTO.map((f) => {
      let valorRecebido = parseFloat(String(pagamentos[f.key]).replace(",", ".")) || 0;

      if (f.key === "DINHEIRO" && troco > 0) {
        valorRecebido = Math.max(valorRecebido - troco, 0);
      }

      return {
        "CLI_ID": 1,
        "VEN_ID": 1,
        "CRE_PAGAMENTO": f.label.toUpperCase(),
        "CRE_DTA_VENCIMENTO": vencimento,
        "CRE_VLR_RECEBER": valorRecebido,
        "CRE_STATUS": "Em Aberto",
        "CRE_VLR_DESCONTO": 0,
        "CRE_TOT_DIFERENCA": valorRecebido,
        "CRE_OCULTAR": "T"
      };
    }).filter((p) => p.CRE_VLR_RECEBER > 0);

    const formasUsadas = FORMAS_PAGAMENTO.filter((f) => {
      const v = parseFloat(String(pagamentos[f.key]).replace(",", ".")) || 0;
      return v > 0;
    }).map((f) => ({
      label: f.label,
      valor: parseFloat(String(pagamentos[f.key]).replace(",", ".")) || 0,
    }));

    const agora = new Date();
    const dataFormatada = agora.toLocaleDateString("pt-BR");
    const horaFormatada = agora.toLocaleTimeString("pt-BR");

    // ---------- Função que envia a venda pro backend ----------
    const venda = async () => {
      try {
        const res = await fetch(`${userInfo.url}/nfce`, {
          method: "POST",
          headers: {
            'Content-Type': 'application/json',
            'empresa': userInfo.alias,
            'Authorization': `Basic ${credentials}`,
          },
          body: JSON.stringify({
            "CLI_ID": 1,
            "VEN_ID": 1,
            "CUP_STATUS": "Emitido",
            "CUP_TOT_CUPOM": totalComDesconto,
            "CUP_TOT_DESCONTO": descontoNum,
            "CUP_TOT_MERCADORIA": totalCompra,
            "CUP_TOT_RECEBIDO": totalPago,
            "CUP_TOT_TROCO": troco,
            "USU_ID": 1,
            "ITEM_NFCE": itensValidosFormatados,
            "RECEBER": pagamentosFormatados
          })
        });

        if (res.ok) {
          alert("Venda Finalizada com sucesso!");
        } else {
          alert("Venda Finalizada com sucesso!");
        }
      } catch (error) {
        console.error("Erro ao finalizar venda:", error);
      }
    };

    // ---------- Pergunta antes de imprimir ----------
    const desejaImprimir = window.confirm(
      "Venda finalizada! Deseja imprimir o ticket agora?"
    );

    if (desejaImprimir) {
      const win = window.open("", "_blank", "width=600,height=600");

      win.document.write(`
        <html>
        <head>
          <title>Ticket de Venda</title>
          <style>
            @page { size: 80mm auto; margin: 0; }
            html, body { width: 80mm; margin: 0; padding: 0; }
            body { font-family: monospace; font-size: 12px; padding: 2mm 3mm; box-sizing: border-box; }
            .logo { display:block; margin: 0 auto 8px; max-width: 100%; }
            h2 { text-align:center; margin: 4px 0; }
            .datahora { text-align:center; margin-bottom: 6px; }
            .linha { display:flex; justify-content:space-between; word-break: break-word; }
            hr { border: none; border-top: 1px dashed #000; }
            .total { font-size: 14px; font-weight:bold; }
            .pagamentos { margin-top: 4px; }
          </style>
        </head>
        <body onload="window.print(); window.close();">
          <img class="logo" src="${userInfo.url}/logos/${userInfo.alias}/${userInfo.alias}.bmp" />
          <h2>${userInfo.company}</h2>
          <div class="datahora">${dataFormatada} ${horaFormatada}</div>
          <hr/>
          ${itensValidos
            .map(
              (it) => `
            <div class="linha"><span>${it.qtd}x ${it.produto}</span><span>R$ ${fmt(it.total)}</span></div>`
            )
            .join("")}
          <hr/>
          <div class="linha"><span>Subtotal</span><span>R$ ${fmt(totalCompra)}</span></div>
          <div class="linha"><span>Desconto</span><span>R$ ${fmt(descontoNum)}</span></div>
          <div class="linha total"><span>TOTAL</span><span>R$ ${fmt(totalComDesconto)}</span></div>
          <hr/>
          <div class="pagamentos">
            ${formasUsadas
              .map(
                (f) => `
              <div class="linha"><span>${f.label}</span><span>R$ ${fmt(f.valor)}</span></div>`
              )
              .join("")}
          </div>
          ${
            troco > 0
              ? `<hr/><div class="linha total"><span>TROCO</span><span>R$ ${fmt(troco)}</span></div>`
              : ""
          }
          <hr/>
          <p style="text-align:center;">Obrigado pela preferência!</p>
        </body>
        </html>
      `);
      win.document.close();
    }

    // ---------- Envia pro backend e limpa a tela ----------
    venda();

    setItens([]);
    setDesconto("0");
    setPagamentos(FORMAS_PAGAMENTO.reduce((acc, f) => ({ ...acc, [f.key]: "" }), {}));
    setModalPagamentoAberto(false);
    setErroPagamento("");
    focarBusca();
  }, [totalPago, totalComDesconto, itens, descontoNum, totalCompra, pagamentos, userInfo, focarBusca, troco, credentials]);

  const handleKeyDownQtdOuValor = (e) => {
    if (e.key === "Enter") {
      adicionarItem();
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "F2") {
        e.preventDefault();
        if (modalPagamentoAberto) {
          finalizarVenda();
        } else {
          abrirModalPagamento();
        }
      }

      if (e.key === "Escape") {
        e.preventDefault();
        if (modalPagamentoAberto) {
          voltarDoPagamento();
        }
      }

      if (e.key === "Escape") {
        e.preventDefault();
        if (modalAberto) {
          setModalAberto(null);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [modalPagamentoAberto, abrirModalPagamento, finalizarVenda, voltarDoPagamento, modalAberto]);

  return (
    <div className="pdv-container">
      {/* PAINEL ESQUERDO */}
      <aside className="pdv-left">
        <div className="btnMenu">
          <MenuTopo onAbrirModal={setModalAberto} />
          <button className="menu-btn" onClick={() => navigate("/")}> <GiExitDoor size={18} /> Sair</button>
        </div>

        <div className="pdv-autocomplete">
          <input
            ref={buscaRef}
            className="pdv-input pdv-input-lg"
            value={busca}
            onChange={(e) => buscarUnificado(e.target.value)}
            onKeyDown={handleKeyDownBusca}
            placeholder="Digite código, código de barras ou nome..."
            autoFocus
          />
          {sugestoes.length > 0 && (
            <ul className="pdv-suggestions">
              {sugestoes.map((p) => (
                <li key={p.proId} onClick={() => selecionarSugestao(p)}>
                  <span>{p.proDescricao}</span>
                  <small>R$ {fmt(p.proVlrVenda)}</small>
                </li>
              ))}
            </ul>
          )}
          <p className="pdv-descricao">{descricao || "—"}</p>
        </div>

        <div className="pdv-row">
          <div className="pdv-col">
            <label className="pdv-label">QUANTIDADE</label>
            <input
              ref={qtdRef}
              type="text"
              inputMode="decimal"
              className="pdv-input pdv-input-num"
              value={quantidade}
              onChange={(e) => setQuantidade(e.target.value.replace(/[^0-9,]/g, ""))}
              onKeyDown={handleKeyDownQtdOuValor}
            />
          </div>
          <div className="pdv-col">
            <label className="pdv-label">VALOR</label>
            <input
              ref={valorRef}
              type="number"
              className="pdv-input pdv-input-num"
              value={valor}
              onChange={(e) => setValor(e.target.value)}
              onKeyDown={handleKeyDownQtdOuValor}
              min="0"
              step="0.01"
            />
          </div>
        </div>

        {erro && <div className="pdv-erro">{erro}</div>}

        <button className="pdv-btn-add" onClick={adicionarItem}>
          Adicionar Item (Enter)
        </button>

        {userInfo?.url && userInfo?.alias && (
          <img
            src={`${userInfo.url}/logos/${userInfo.alias}/${userInfo.alias}.bmp`}
            alt="Logo da empresa"
            className="logoHeader"
          />
        )}

        <div className="pdv-subtotal-box">
          <div>
            <label className="pdv-label">DESCONTO (R$)</label>
            <input
              className="pdv-input pdv-input-desconto"
              value={desconto}
              onChange={(e) => setDesconto(e.target.value.replace(/[^0-9,]/g, ""))}
            />
          </div>

          <div className="pdv-total-box">
            <span>TOTAL COMPRA</span>
            <strong>R$ {fmt(totalComDesconto)}</strong>
          </div>

          <button className="pdv-btn-finalizar" onClick={abrirModalPagamento}>
            Finalizar Venda (F2)
          </button>
        </div>
      </aside>

      {/* PAINEL DIREITO - LISTA DE ITENS */}
      <main className="pdv-right">
        {loadingProdutos && <div className="pdv-loading">Carregando produtos...</div>}
        <table className="pdv-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>PRODUTO</th>
              <th>QTD</th>
              <th>VALOR</th>
              <th>TOTAL</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {itens.length === 0 ? (
              <tr>
                <td colSpan={6} className="pdv-empty">
                  Nenhum item lançado
                </td>
              </tr>
            ) : (
              itens.map((it, idx) => (
                <tr
                  key={it.id}
                  className={it.removido ? "pdv-row-removida" : ""}
                  onClick={() => toggleRemovido(it.id)}
                >
                  <td>{idx + 1}</td>
                  <td>{it.produto}</td>
                  <td>{it.qtd}</td>
                  <td>R$ {fmt(it.valor)}</td>
                  <td>R$ {fmt(it.total)}</td>
                  <td>{it.removido ? "🗑️ Retirado" : ""}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </main>

      {/* MODAL DE PAGAMENTO */}
      {modalPagamentoAberto && (
        <div className="modal-overlay">
          <div className="modal-box modal-pagamento">
            <div className="modal-header">
              <h3>Forma de Pagamento</h3>
              <button className="btn-close" onClick={voltarDoPagamento}>✕</button>
            </div>

            <div className="modal-pagamento-body">
              <div className="pdv-total-box" style={{ marginBottom: 16 }}>
                <span>TOTAL A PAGAR</span>
                <strong>R$ {fmt(totalComDesconto)}</strong>
              </div>

              {FORMAS_PAGAMENTO.map((f, idx) => (
                <div key={f.key} className="pdv-row" style={{ marginBottom: 10 }}>
                  <label className="pdv-label" style={{ minWidth: 90 }}>{f.label}</label>
                  <input
                    ref={idx === 0 ? dinheiroRef : null}
                    type="text"
                    inputMode="decimal"
                    className="pdv-input pdv-input-num"
                    placeholder="0,00"
                    value={pagamentos[f.key]}
                    onChange={(e) => {
                      const v = e.target.value.replace(/[^0-9,]/g, "");
                      handlePagamentoChange(f.key, v);
                    }}
                  />
                </div>
              ))}

              <hr />

              <div className="pdv-row" style={{ justifyContent: "space-between" }}>
                <span>Total Pago:</span>
                <strong>R$ {fmt(totalPago)}</strong>
              </div>

              {faltaPagar > 0 && (
                <div className="pdv-row" style={{ justifyContent: "space-between", color: "#c0392b" }}>
                  <span>Falta pagar:</span>
                  <strong>R$ {fmt(faltaPagar)}</strong>
                </div>
              )}

              {troco > 0 && (
                <div className="pdv-row" style={{ justifyContent: "space-between", color: "#27ae60" }}>
                  <span>Troco:</span>
                  <strong>R$ {fmt(troco)}</strong>
                </div>
              )}

              {erroPagamento && <div className="pdv-erro">{erroPagamento}</div>}

              <div className="pdv-row-buttons" style={{ marginTop: 18, gap: 10 }}>
                <button className="pdv-btn-add" onClick={voltarDoPagamento}>
                  ← Voltar (Esc)
                </button>
                <button className="pdv-btn-finalizar" onClick={finalizarVenda}>
                  Confirmar e Imprimir (F2)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAIS */}
      {modalAberto === "produtos" && (
        <ModalProdutos
          produtos={produtos}
          onClose={() => setModalAberto(null)}
          onProdutoSalvo={fetchProdutos}
        />
      )}

      {modalAberto === "vendas" && (
        <ModalCaixa
          onClose={() => setModalAberto(null)}
        />
      )}

      {modalAberto === "xml" && (
        <ModalXml onClose={() => setModalAberto(null)} />
      )}

      {modalAberto === "relatorios" && (
        <ModalRelatorios onClose={() => setModalAberto(null)} />
      )}

      {modalAberto === "config" && (
        <ModalConfigEmpresa onClose={() => setModalAberto(null)} />
      )}
    </div>
  );
}